import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BookDirect } from "@/components/CTAButtons";
import { NAV, HOTEL } from "@/data/site";

const MORE = [
  { label: "Business Travel", to: "/business-travel" },
  { label: "Family Travel", to: "/family-travel" },
  { label: "Group Bookings", to: "/group-bookings" },
  { label: "Nearby Cities", to: "/areas" },
  { label: "Blog", to: "/blog" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed inset-x-0 top-0 z-[90] border-b border-charcoal/10 bg-paper/90 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-3 md:px-10">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={`font-body text-sm font-medium uppercase tracking-wide transition-colors duration-200 hover:text-brand-red ${
                  pathname === n.to ? "text-brand-red" : "text-charcoal/75"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={`tel:${HOTEL.phone}`}
              data-testid="nav-call"
              className="hidden items-center gap-2 font-body text-sm font-semibold text-charcoal transition-colors hover:text-brand-red md:flex"
            >
              <Phone className="h-4 w-4" />
              {HOTEL.phoneDisplay}
            </a>
            <div className="hidden sm:block">
              <BookDirect label="Book" testid="nav-book-direct" className="px-5 py-2.5 text-sm" />
            </div>
            <button
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center border border-charcoal/20 text-charcoal lg:hidden"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] flex flex-col bg-paper px-6 pb-10 pt-24 lg:hidden"
          >
            <nav className="flex flex-col gap-1 overflow-y-auto">
              {[...NAV, ...MORE].map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={n.to}
                    data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                    className="block border-b border-charcoal/10 py-4 font-heading text-2xl font-black uppercase text-charcoal"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8">
              <BookDirect label="Book Direct" className="w-full justify-center py-4 text-base" testid="mobile-book-direct" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
